/**
 * Parse best-effort de string de endereço livre → dict estruturado
 * com chaves esperadas pelo backend (`logradouro`, `numero`, `complemento`,
 * `bairro`, `cidade`, `uf`, `cep`).
 *
 * Desde 08/10 a sede nova da alteração é digitada no `EnderecoForm`; isto só
 * roda para rascunho ANTIGO, que tem o texto em `endereco` e os 7 campos vazios:
 * ao reabrir, o texto entra no formulário (`sedeDoRascunhoAntigo`, index.tsx).
 * O backend exige dict pra `PatchSubstituirCampo(campo='endereco')`.
 *
 * Limitações conhecidas:
 * - Endereços brasileiros têm formato razoavelmente padronizado mas com
 *   muitas variações. O parser cobre os formatos mais comuns; em casos
 *   atípicos, `logradouro` recebe a string inteira e demais campos ficam
 *   vazios — backend renderiza, fica feio mas não 500.
 * - Não tenta interpretar abreviações (Av./Rua/etc) — preserva como está.
 */

export interface IEnderecoParseado {
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  cep: string;
}

const VAZIO: IEnderecoParseado = {
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
  cep: '',
};

/**
 * Tenta extrair CEP da string. Aceita formatos `XXXXX-XXX`, `XX.XXX-XXX`,
 * `XXXXXXXX`. Devolve normalizado `XXXXX-XXX`.
 */
function extrairCep(s: string): { cep: string; resto: string } {
  // 5 digitos + opcional -/. + 3 digitos. Aceita "CEP: 74.770-030", "CEP 74770030", "74770-030".
  const rx = /\b(?:CEP[:\s]*)?(\d{2}\.?\d{3}-?\d{3})\b/i;
  const m = s.match(rx);
  if (!m) return { cep: '', resto: s };
  // Remove TUDO que não é dígito — antes só removia pontos e espaços, deixando
  // o hífen do CEP de origem (`75.460-016` virava `75460-016`) e a reformatação
  // produzia `75460--016` (hífen duplicado). Bug reproduzido 2026-06-03.
  const cepRaw = m[1].replace(/\D/g, '');
  const cepNorm = `${cepRaw.slice(0, 5)}-${cepRaw.slice(5)}`;
  const resto = s.replace(m[0], '').trim();
  return { cep: cepNorm, resto };
}

/**
 * Tenta extrair UF (2 letras maiúsculas). Procura no fim do endereço,
 * geralmente após `-` ou `–`. Devolve UF + resto sem a marca.
 */
function extrairUf(s: string): { uf: string; resto: string } {
  // Caso 1: "...Cidade - UF" ou "...Cidade – UF" no final.
  const rxFim = /[-–]\s*([A-Z]{2})\b\s*$/;
  const m1 = s.match(rxFim);
  if (m1) {
    return {
      uf: m1[1],
      resto: s
        .slice(0, m1.index)
        .trim()
        .replace(/[,.\s]+$/, ''),
    };
  }
  // Caso 2: "...Cidade-UF" ou "...Cidade/UF" sem espaço.
  const rxCol = /\b([A-Z]{2})\b\s*(?:,|$)/;
  const m2 = s.match(rxCol);
  if (m2 && m2.index !== undefined && m2.index > s.length / 2) {
    // Só aceita se aparece na 2ª metade (heurística pra evitar match em
    // "RJ" no meio da rua, etc).
    const before = s
      .slice(0, m2.index)
      .trim()
      .replace(/[-–,.\s]+$/, '');
    const after = s.slice(m2.index + m2[0].length).trim();
    return { uf: m2[1], resto: after ? `${before}, ${after}` : before };
  }
  return { uf: '', resto: s };
}

/**
 * Parse best-effort. Aceita variações comuns; em caso de falha,
 * `logradouro` recebe a string inteira e demais campos ficam vazios
 * (backend ainda aceita — render pode ficar feio).
 *
 * Exemplos cobertos:
 * - "Avenida Anápolis, nº 2662, quadra J, lote 07, Vila Pedroso, Goiânia – GO, CEP: 74.770-030"
 * - "Rua X, 100, Bairro Y, Cidade - SP, CEP 01000-000"
 * - "Av. JK, s/n, Qd. 01, Lt. 10, Centro, Goiânia – GO, CEP: 75460-016"
 */
export function parsearEnderecoLivre(raw: string | null | undefined): IEnderecoParseado {
  if (!raw || !raw.trim()) return { ...VAZIO };
  let s = raw.trim();

  const { cep, resto: semCep } = extrairCep(s);
  s = semCep;

  const { uf, resto: semUf } = extrairUf(s);
  s = semUf;

  // Quebra em partes separadas por vírgula. Cada parte é um "token".
  const partes = s
    .split(',')
    .map(p => p.trim())
    .filter(p => p.length > 0);

  if (partes.length === 0) {
    return { ...VAZIO, logradouro: raw.trim(), cep, uf };
  }

  // Heurística:
  // - logradouro = 1ª parte
  // - número = 2ª parte se for "nº X", "s/n", "X", ou começar com "número"
  // - cidade = última parte
  // - bairro = penúltima parte (se mais de 3 partes)
  // - complemento = partes intermediárias entre número e bairro/cidade

  const logradouro = partes[0] ?? '';
  let numero = '';
  let complemento = '';
  let bairro = '';
  let cidade = '';

  // Detecta número na 2ª parte
  let cursor = 1;
  if (partes.length > 1) {
    const p2 = partes[1];
    // "no" e "número" só valem sozinhos: bairro começado por "No" (Nossa Senhora, Nova Suíça,
    // Norte Ferroviário) virava número ("ssa Senhora de Fátima").
    const rxPrefixo = /^(?:nº|n\.?º|n\.?o(?![a-zà-ÿ])|n[uú]mero(?![a-zà-ÿ]))[.:\s]*/i;
    if (rxPrefixo.test(p2) || /^s\/?n$/i.test(p2) || /^\d+[a-zA-Z\-/]*$/.test(p2)) {
      numero = p2.replace(rxPrefixo, '').trim();
      cursor = 2;
    }
  }

  // Última parte = cidade
  if (partes.length > cursor) {
    cidade = partes[partes.length - 1];
  }

  // Penúltima parte = bairro (se sobrou espaço entre número e cidade)
  if (partes.length > cursor + 1) {
    bairro = partes[partes.length - 2];
  }

  // Demais partes intermediárias = complemento
  if (partes.length > cursor + 2) {
    complemento = partes.slice(cursor, partes.length - 2).join(', ');
  }

  // Edge case: 2 partes (logradouro + número, sem cidade) → logradouro+número, cidade vazia.
  // Já tratado por cidade ficar vazio se partes.length<=cursor.

  // Edge case: 2 partes mas a 2ª não é número → é cidade.
  if (cursor === 1 && partes.length === 2) {
    [, cidade] = partes;
  }

  return {
    logradouro,
    numero,
    complemento,
    bairro,
    cidade,
    uf,
    cep,
  };
}
